import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LabelStyled } from 'approot/shared/styles/inputs.style';
import SimpleModal from 'lib/modal/simple-modal';
import { SimpleModalContentStyled } from 'lib/modal/simple-modal.styles';
import * as Styled from '../../print/activity-builder-print-modal.style';
import { spacing4 } from 'approot/shared/styles/spacing.style';
import {
  CheckableSinglePill,
  CheckableSingleGroup,
} from 'approot/shared/styles/buttons/checkable-button.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import { useGetInteractiveLessonParams } from '../../activity-builder.hooks';
import { useGetAssessmentActivities } from '../assessment-builder.hooks';
import { useGetActivitySections } from '../../activity-builder-section.hooks';
import AssessmentBuilderA4 from './assessment-builder-a4';
import {
  INTRODUCTION_INDEX,
  LANDSCAPE,
  Orientation,
  PORTRAIT,
  useActivityBuilderApollo,
} from '../../activity-builder.apollo';
import Pagination from 'approot/shared/pagination/pagination';
import DropdownMultiselect from 'approot/shared/dropdown/dropdown-multiselect';
import {
  ALL_WORD,
  ASSESSMENT_ACTIVITIES,
  LANGUAGE_CODE_EN_US,
} from 'global/constants';
import { getNewFilterSelection } from 'approot/shared/filters/filter-option/filter-option.utils';
import { ACTIVITY_BUILDER_A4_LONG_SIDE } from '../../activity-builder.constants';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import { GET_ACTIVITIES_PDF_UPLOAD_URL } from '../../print/activity-buider-print.graphql';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import type {
  GetActivitiesPdfUploadUrlQuery,
  GetActivitiesPdfUploadUrlQueryVariables,
} from '../../print/activity-buider-print.graphql.generated';
import useUserData from 'approot/shared/user/user-data';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { getSectionsAndSubSectionsOverview } from '../../activity-builder.utils';
import ErrorMessage from 'approot/shared/error-message/error-message';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { AssessmentBuilderPrintGenerator } from './assessment-builder-print-generator';
import { InvisiblePrintWrapper } from '../../print/activity-builder-print-generator.style';
import { Box } from 'approot/shared/design-system/box/box.style';

type Props = {
  closeModal: () => void;
};

const SLIDES_FIELD = 'slides';
const PAGE_PRE_RENDER = 'prerender';

const AssessmentBuilderPrintModal = ({ closeModal }: Props) => {
  const { contentId, contentType, version, userClassId } =
    useGetInteractiveLessonParams();

  const { user } = useUserData();
  const { showError, showSuccess } = useSnackbar();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [initialisedSlides, setInitialisedSlides] = useState<boolean>(false);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  const { data, loading, error } = useGetAssessmentActivities({
    contentId,
    version,
    classId: userClassId,
  });

  const {
    data: sectionsData,
    loading: sectionsLoading,
    error: sectionsError,
  } = useGetActivitySections({
    contentId,
    contentType,
    classId: userClassId,
    sectionIds: data?.sectionsOverview.map(s => s.id),
    skip: !data?.sectionsOverview,
    version,
  });

  const interactiveAssessment = data;
  const sections = sectionsData?.getActivitiesSections;

  const [getActivitiesPdfUploadUrl] = useLoggingLazyQuery<
    GetActivitiesPdfUploadUrlQuery,
    GetActivitiesPdfUploadUrlQueryVariables
  >(GET_ACTIVITIES_PDF_UPLOAD_URL);

  const {
    data: {
      printSettings: { orientation, slidesPerPage, slides },
    },
    setPrintSettings,
  } = useActivityBuilderApollo();

  const slideIndexes = useMemo(() => {
    return [
      INTRODUCTION_INDEX,
      ...(interactiveAssessment?.sectionsOverview
        ? getSectionsAndSubSectionsOverview(
            interactiveAssessment.sectionsOverview
          ).map((_, index) => index)
        : []),
    ];
  }, [interactiveAssessment?.sectionsOverview]);

  const handleZoom = (newOrientation?: Orientation) => {
    const container = containerRef.current;
    const orientationSetting = newOrientation || orientation;

    if (container) {
      if (orientationSetting === LANDSCAPE) {
        const ratio = 508 / ACTIVITY_BUILDER_A4_LONG_SIDE;

        container.style.transform = `scale(${ratio})`;
        container.style.transformOrigin = 'left top';
        container.style.width = 'auto';
        container.style.height = 'auto';
      } else {
        const ratio = 361.17 / ACTIVITY_BUILDER_A4_LONG_SIDE;

        container.style.transform = `scale(${ratio}) translateX(-50%)`;
        container.style.transformOrigin = 'center top';
        container.style.width = 'auto';
        container.style.height = 'auto';
      }
    }
  };

  useEffect(() => {
    if (!initialisedSlides && slideIndexes.length > 0) {
      setPrintSettings({
        slides: slideIndexes,
      });
      setInitialisedSlides(true);
    }
  }, [initialisedSlides, slideIndexes, setPrintSettings]);

  const totalPages =
    slidesPerPage === 2
      ? Math.ceil(slides.filter(s => s !== INTRODUCTION_INDEX).length / 2) +
        (slides.includes(INTRODUCTION_INDEX) ? 1 : 0)
      : slides.length;

  const handlePrint = useCallback(async () => {
    if (slides.length && user && interactiveAssessment && sections) {
      setIsPrinting(true);

      const generate = await import('../../print/activity-builder-print');
      const container = document.getElementById(PAGE_PRE_RENDER);
      if (container) {
        const contentString = generate.renderAndCollectStyles(
          AssessmentBuilderPrintGenerator({
            sections,
            interactiveAssessment,
            slidesPerPage,
            orientation,
            slides,
            language: LANGUAGE_CODE_EN_US,
            version,
          }),
          container.outerHTML
        );

        getActivitiesPdfUploadUrl({
          variables: {
            input: {
              contentId,
              contentType: ASSESSMENT_ACTIVITIES,
              version,
              language: LANGUAGE_CODE_EN_US,
            },
          },
        })
          .then(async res => {
            const data = res.data?.getActivitiesPdfUploadUrl;
            const url = data?.url;

            await fetch(url || '', {
              method: 'PUT',
              body: contentString,
            });
            closeModal();
            showSuccess('Your PDF Assessment will be emailed to you shortly.');
          })
          .catch(() => {
            showError('There was an error printing the PDF');
            setIsPrinting(false);
          });
      }
    }
  }, [
    interactiveAssessment,
    contentId,
    orientation,
    slides,
    slidesPerPage,
    user,
    getActivitiesPdfUploadUrl,
    closeModal,
    showError,
    showSuccess,
    sections,
    version,
  ]);

  const hasError = error || sectionsError;
  const isLoading = loading || sectionsLoading;

  return (
    <SimpleModal
      isVisible
      title="Email PDF Assessment"
      headerTitle="Email PDF Assessment"
      size={864}
      onClose={closeModal}
    >
      <SimpleModalContentStyled>
        {hasError ? (
          <ErrorMessage centreText>
            There was an error loading this assessment&rsquo;s print preview.
            Please refresh the page and try again.
          </ErrorMessage>
        ) : isLoading ? (
          <Bouncer />
        ) : (
          <Styled.PrintModalContainer>
            <Styled.PrintModalPrintPreviewContainer>
              <Styled.PrintModalPrintPreview>
                <Styled.PrintModalPrintPreviewContents
                  ref={ref => {
                    if (!containerRef.current) {
                      containerRef.current = ref;
                      handleZoom();
                    }
                  }}
                >
                  <Styled.PrintModalPrintPreviewA4>
                    {interactiveAssessment && sections && (
                      <AssessmentBuilderA4
                        pageIndex={currentPage}
                        interactiveAssessment={interactiveAssessment}
                        sections={sections}
                        contentVersion={version}
                      />
                    )}
                  </Styled.PrintModalPrintPreviewA4>
                </Styled.PrintModalPrintPreviewContents>
              </Styled.PrintModalPrintPreview>

              {slides.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  onPrevious={() => setCurrentPage(currentPage - 1)}
                  onNext={() => setCurrentPage(currentPage + 1)}
                  totalPageCount={totalPages}
                />
              )}
            </Styled.PrintModalPrintPreviewContainer>

            <Styled.PrintModalOptions>
              <Box $flexDirection="column" $marginBottom={24}>
                <LabelStyled htmlFor={SLIDES_FIELD} marginBottom={spacing4}>
                  Select the activities to email
                </LabelStyled>
                <DropdownMultiselect
                  id={SLIDES_FIELD}
                  label="Activities"
                  options={[
                    {
                      value: ALL_WORD,
                      label: 'All Activities',
                    },
                    ...slideIndexes.map(index => {
                      return {
                        value: `${index}`,
                        label:
                          index === INTRODUCTION_INDEX
                            ? 'Cover'
                            : `Activity ${index + 1}`,
                      };
                    }),
                  ]}
                  dataSelector={SLIDES_FIELD}
                  selectedDropdownOptions={slides.map(slide => `${slide}`)}
                  handleChangeCheckbox={(value: string) => {
                    const updatedSlides = getNewFilterSelection(
                      slides.map(s => `${s}`),
                      value
                    ).map(s => Number(s));

                    setPrintSettings({
                      slides: updatedSlides,
                    });

                    setCurrentPage(0);
                  }}
                  isInline
                />
              </Box>

              {slides.length > 0 &&
                orientation === PORTRAIT &&
                interactiveAssessment?.sectionsOverview &&
                interactiveAssessment.sectionsOverview.length > 1 && (
                  <Box $flexDirection="column" $marginBottom={24}>
                    <LabelStyled htmlFor="slides">
                      Activities per page
                    </LabelStyled>

                    <CheckableSingleGroup>
                      <CheckableSinglePill
                        $isRound
                        aria-label="Set activities per page to 1"
                        aria-checked={slidesPerPage === 1}
                        $isSelected={slidesPerPage === 1}
                        onClick={() => {
                          setPrintSettings({
                            slidesPerPage: 1,
                          });
                          setCurrentPage(0);
                        }}
                      >
                        1
                      </CheckableSinglePill>
                      <CheckableSinglePill
                        $isRound
                        aria-label="Set activities per page to 2"
                        aria-checked={slidesPerPage === 2}
                        $isSelected={slidesPerPage === 2}
                        onClick={() => {
                          setPrintSettings({
                            slidesPerPage: 2,
                          });
                          setCurrentPage(0);
                        }}
                      >
                        2
                      </CheckableSinglePill>
                    </CheckableSingleGroup>
                  </Box>
                )}
              {interactiveAssessment && (
                <InvisiblePrintWrapper id={PAGE_PRE_RENDER}>
                  <AssessmentBuilderPrintGenerator
                    interactiveAssessment={interactiveAssessment}
                    sections={sections || []}
                    slidesPerPage={slidesPerPage}
                    orientation={orientation}
                    slides={slides}
                    language={LANGUAGE_CODE_EN_US}
                    version={version}
                  />
                </InvisiblePrintWrapper>
              )}

              <PrimaryButton
                type="button"
                loading={isPrinting}
                disabled={!slides.length || isPrinting}
                onClick={async () => {
                  handlePrint();
                }}
              >
                Email PDF Assessment
              </PrimaryButton>

              <PStyled textAlignCenter>
                We'll email a PDF of this assessment to{' '}
                <strong>{user?.email}</strong>
              </PStyled>
            </Styled.PrintModalOptions>
          </Styled.PrintModalContainer>
        )}
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export default AssessmentBuilderPrintModal;
